import { useState, useMemo, useEffect } from "react";
import PageHeader from "@/components/base/PageHeader";
import { SkeletonBlock } from "@/components/base/Skeleton";
import { listSessions, getAssetCurve, type Session, type CurvePoint } from "@/api/alpha";

type CalendarMode = "day" | "week" | "month";

const weekDayLabels = ["一", "二", "三", "四", "五", "六", "日"];

// Per-day calendar cell. Derived from the session asset curve:
//   profit    = totalAsset(day) - totalAsset(prevDay)   (money)
//   rate      = dailyReturn                              (percent, e.g. 0.18)
//   cumProfit = totalAsset(day) - fundAmount            (money)
//   cumRate   = cumProfit / fundAmount * 100            (percent)
interface DayCell {
  day: number;
  weekDay: string;
  profit: number | null;
  rate: number | null;
  isTrade: boolean;
  cumProfit: number | null;
  cumRate: number | null;
}

interface MonthDaily {
  year: number;
  month: number;
  label: string;
  daysInMonth: number;
  startDayIndex: number;
  daily: DayCell[];
}

interface MonthRow {
  month: string;
  profit: number;
  rate: number;
  days: number;
  cumProfit: number;
  cumRate: number;
  sortKey: number;
}

interface PerDay {
  date: string;
  profit: number;
  rate: number;
  totalAsset: number;
  year: number;
  monthNum: number;
  day: number;
}

interface CalendarData {
  monthsData: Record<number, MonthDaily>;
  summaryCache: Record<number, { profit: number; rate: number; days: number }>;
  monthRows: MonthRow[];
  availableMonths: number[];
  latestYear: number;
  latestMonth: number;
}

interface WeekGroup {
  label: string;
  startDay: number;
  endDay: number;
  profit: number;
  rate: number;
  cumProfit: number | null;
  cumRate: number | null;
  days: DayCell[];
}

const isWeekday = (year: number, month: number, day: number): boolean => {
  const dow = new Date(year, month - 1, day).getDay();
  return dow !== 0 && dow !== 6;
};

function buildMonthDaily(year: number, month: number, entries: PerDay[], fund: number): MonthDaily {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = new Date(year, month - 1, 1).getDay();
  const startDayIndex = (firstDow + 6) % 7;
  const map = new Map(entries.map((e) => [e.day, e]));
  const daily: DayCell[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(year, month - 1, d).getDay();
    const wdIdx = (dow + 6) % 7;
    const weekDay = weekDayLabels[wdIdx];
    const weekend = dow === 0 || dow === 6;
    const e = map.get(d);
    if (e && !weekend) {
      daily.push({
        day: d,
        weekDay,
        profit: e.profit,
        rate: e.rate,
        isTrade: true,
        cumProfit: e.totalAsset - fund,
        cumRate: fund > 0 ? ((e.totalAsset - fund) / fund) * 100 : 0,
      });
    } else {
      daily.push({ day: d, weekDay, profit: null, rate: null, isTrade: !weekend, cumProfit: null, cumRate: null });
    }
  }
  return { year, month, label: `${month}月`, daysInMonth, startDayIndex, daily };
}

// Reshape the session asset curve into the calendar's month/week/day views.
function buildCalendar(session: Session, curve: CurvePoint[]): CalendarData | null {
  const fund = session.fundAmount > 0 ? session.fundAmount : 1;
  let prev = fund;
  const perDay: PerDay[] = [];
  for (const p of curve) {
    const profit = p.totalAsset - prev;
    prev = p.totalAsset;
    const [y, m, d] = p.date.split("-").map(Number);
    perDay.push({ date: p.date, profit, rate: p.dailyReturn, totalAsset: p.totalAsset, year: y, monthNum: m, day: d });
  }
  if (perDay.length === 0) return null;

  const last = perDay[perDay.length - 1];
  const latestYear = last.year;
  const latestMonth = last.monthNum;

  const groups = new Map<number, PerDay[]>();
  for (const pd of perDay) {
    const k = pd.year * 100 + pd.monthNum;
    const arr = groups.get(k);
    if (arr) arr.push(pd);
    else groups.set(k, [pd]);
  }

  const monthsData: Record<number, MonthDaily> = {};
  const summaryCache: Record<number, { profit: number; rate: number; days: number }> = {};
  const monthRows: MonthRow[] = [];

  for (const [k, entries] of groups) {
    const y = Math.floor(k / 100);
    const m = k % 100;
    const weekdayEntries = entries.filter((e) => isWeekday(e.year, e.monthNum, e.day));
    const monthProfit = weekdayEntries.reduce((s, e) => s + e.profit, 0);
    const firstEntry = entries[0];
    const startAsset = firstEntry.totalAsset - firstEntry.profit;
    const monthRate = startAsset > 0 ? (monthProfit / startAsset) * 100 : 0;
    const lastEntry = entries[entries.length - 1];
    const cumProfit = lastEntry.totalAsset - fund;
    const cumRate = fund > 0 ? ((lastEntry.totalAsset - fund) / fund) * 100 : 0;
    const days = weekdayEntries.length;

    monthRows.push({ month: `${m}月`, profit: monthProfit, rate: monthRate, days, cumProfit, cumRate, sortKey: k });

    if (y === latestYear) {
      summaryCache[m] = { profit: monthProfit, rate: monthRate, days };
      monthsData[m] = buildMonthDaily(y, m, entries, fund);
    }
  }

  monthRows.sort((a, b) => b.sortKey - a.sortKey);
  const availableMonths = Object.keys(monthsData)
    .map(Number)
    .sort((a, b) => a - b);

  return { monthsData, summaryCache, monthRows, availableMonths, latestYear, latestMonth };
}

function computeWeeks(daily: DayCell[], month: number, year: number): WeekGroup[] {
  const weeks: WeekGroup[] = [];
  let currentWeek: DayCell[] = [];
  const monthLabel = String(month).padStart(2, "0");

  for (const d of daily) {
    currentWeek.push(d);
    const date = new Date(year, month - 1, d.day);
    const dow = date.getDay();
    if (dow === 0 || d.day === daily[daily.length - 1]?.day) {
      if (currentWeek.length > 0) {
        const tradeDays = currentWeek.filter((x) => x.isTrade && x.profit !== null);
        const weekProfit = tradeDays.reduce((s, x) => s + (x.profit ?? 0), 0);
        const lastTrade = tradeDays[tradeDays.length - 1];
        const firstDay = currentWeek[0].day;
        const lastDay = currentWeek[currentWeek.length - 1].day;
        weeks.push({
          label: `${monthLabel}/${String(firstDay).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
          startDay: firstDay,
          endDay: lastDay,
          profit: weekProfit,
          rate: lastTrade?.cumRate ?? 0,
          cumProfit: lastTrade?.cumProfit ?? null,
          cumRate: lastTrade?.cumRate ?? null,
          days: [...currentWeek],
        });
      }
      currentWeek = [];
    }
  }
  return weeks;
}

function fmtMoney(n: number | null): string {
  if (n === null) return "-";
  const abs = Math.abs(n);
  const sign = n >= 0 ? "+" : "";
  if (abs >= 10000) return sign + (n / 10000).toFixed(2) + "万";
  return sign + n.toFixed(2);
}

function fmtRate(n: number | null): string {
  if (n === null) return "-";
  return (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
}

function colorClass(n: number | null): string {
  if (n === null) return "text-foreground-400";
  if (n > 0) return "text-[#E63946]";
  if (n < 0) return "text-[#2D9F4C]";
  return "text-foreground-400";
}

export default function Calendar() {
  const [mode, setMode] = useState<CalendarMode>("day");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasSession, setHasSession] = useState(true);
  const [data, setData] = useState<CalendarData | null>(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(0);
  const [weekIdx, setWeekIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const { sessions } = await listSessions();
        const s = sessions.find((x) => x.status === "running") || sessions[0];
        if (!s) {
          if (alive) setHasSession(false);
          return;
        }
        if (alive) setHasSession(true);
        // Bound the curve window to the session's lifetime (backend caps at 365).
        const ageDays = Math.ceil((Date.now() / 1000 - s.createdAt) / 86400);
        const daysSpan = Math.min(365, Math.max(14, ageDays + 3));
        const { curve } = await getAssetCurve(s.id, daysSpan);
        const built = buildCalendar(s, curve);
        if (!alive) return;
        setData(built);
        if (built) {
          setCurrentYear(built.latestYear);
          setCurrentMonth(built.latestMonth);
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "加载失败");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const monthsData = data?.monthsData ?? {};
  const summaryCache = data?.summaryCache ?? {};
  const monthRows = data?.monthRows ?? [];
  const availableMonths = data?.availableMonths ?? [];

  const currentMonthData = monthsData[currentMonth];
  const summary = summaryCache[currentMonth];

  const monthProfit = summary?.profit ?? 0;
  const monthRate = summary?.rate ?? 0;
  const monthDays = summary?.days ?? 0;

  const canGoPrev = availableMonths.indexOf(currentMonth) > 0;
  const canGoNext =
    availableMonths.indexOf(currentMonth) >= 0 && availableMonths.indexOf(currentMonth) < availableMonths.length - 1;

  const tradeDays = useMemo(() => {
    if (!currentMonthData) return [];
    return currentMonthData.daily.filter((d) => d.isTrade && d.profit !== null);
  }, [currentMonthData]);

  const weeks = useMemo(() => {
    if (!currentMonthData) return [];
    return computeWeeks(currentMonthData.daily, currentMonth, currentYear);
  }, [currentMonthData, currentMonth, currentYear]);

  const canGoPrevWeek = weekIdx > 0;
  const canGoNextWeek = weekIdx < weeks.length - 1;

  const handlePrevMonth = () => {
    const idx = availableMonths.indexOf(currentMonth);
    if (idx > 0) {
      setCurrentMonth(availableMonths[idx - 1]);
      setWeekIdx(0);
    }
  };

  const handleNextMonth = () => {
    const idx = availableMonths.indexOf(currentMonth);
    if (idx >= 0 && idx < availableMonths.length - 1) {
      setCurrentMonth(availableMonths[idx + 1]);
      setWeekIdx(0);
    }
  };

  const handlePrevWeek = () => {
    if (canGoPrevWeek) setWeekIdx(weekIdx - 1);
  };

  const handleNextWeek = () => {
    if (canGoNextWeek) setWeekIdx(weekIdx + 1);
  };

  const currentWeek = weeks[weekIdx] ?? null;

  if (loading) {
    return (
      <div className="safe-bottom bg-background-50 min-h-screen">
        <PageHeader
          title="收益日历"
          rightAction={
            <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded whitespace-nowrap">
              模拟
            </span>
          }
        />
        <div className="px-4 mb-3">
          <SkeletonBlock className="h-10 rounded-full" />
        </div>
        <div className="px-4 mb-3">
          <div className="bg-white rounded-2xl p-4 space-y-3">
            <SkeletonBlock className="h-6 w-32 mx-auto" />
            <div className="grid grid-cols-3 gap-4">
              <SkeletonBlock className="h-10" />
              <SkeletonBlock className="h-10" />
              <SkeletonBlock className="h-10" />
            </div>
            <SkeletonBlock className="h-40 rounded-xl" />
          </div>
        </div>
        <div className="px-4">
          <SkeletonBlock className="h-48 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="safe-bottom bg-background-50 min-h-screen">
        <PageHeader
          title="收益日历"
          rightAction={
            <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded whitespace-nowrap">
              模拟
            </span>
          }
        />
        <div className="px-4 mt-20 text-center">
          <i className="ri-error-warning-line text-3xl text-foreground-300" />
          <p className="text-sm text-foreground-500 mt-3">{error}</p>
        </div>
      </div>
    );
  }

  if (!hasSession || !data) {
    return (
      <div className="safe-bottom bg-background-50 min-h-screen">
        <PageHeader
          title="收益日历"
          rightAction={
            <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded whitespace-nowrap">
              模拟
            </span>
          }
        />
        <div className="px-4 mt-20 text-center">
          <i className="ri-calendar-line text-4xl text-foreground-300" />
          <p className="text-sm text-foreground-500 mt-3">暂无收益数据</p>
          <p className="text-xs text-foreground-400 mt-1">请先在 AI 看盘开启模拟盘</p>
        </div>
      </div>
    );
  }

  return (
    <div className="safe-bottom bg-background-50 min-h-screen">
      {/* Header */}
      <PageHeader
        title="收益日历"
        rightAction={
          <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded whitespace-nowrap">
            模拟
          </span>
        }
      />

      {/* Segment Control */}
      <div className="px-4 mb-3">
        <div className="flex bg-background-100 rounded-full p-1">
          {(["day", "week", "month"] as CalendarMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex-1 py-2 text-sm rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                mode === m ? "bg-white text-foreground-950" : "text-foreground-500"
              }`}
            >
              {m === "day" ? "按日" : m === "week" ? "按周" : "按月"}
            </button>
          ))}
        </div>
      </div>

      {/* Month Navigator + Calendar Grid */}
      <div className="px-4 mb-3">
        <div className="bg-white rounded-2xl p-4">
          {/* Month selector row */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handlePrevMonth}
              disabled={!canGoPrev}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                canGoPrev ? "bg-background-100 hover:bg-background-200 text-foreground-600" : "bg-background-50 text-foreground-300 cursor-not-allowed"
              }`}
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <span className="text-base font-bold text-foreground-950">
              {currentYear}年{currentMonth}月
            </span>
            <button
              onClick={handleNextMonth}
              disabled={!canGoNext}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                canGoNext ? "bg-background-100 hover:bg-background-200 text-foreground-600" : "bg-background-50 text-foreground-300 cursor-not-allowed"
              }`}
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>

          {/* Summary row - updates with selected month */}
          <div className="grid grid-cols-3 gap-4 py-2 transition-all duration-300">
            <div className="text-center">
              <p className="text-[10px] text-foreground-400 mb-0.5">月收益</p>
              <p className={`text-base font-bold ${colorClass(monthProfit)}`}>
                {fmtMoney(monthProfit)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-foreground-400 mb-0.5">月收益率</p>
              <p className={`text-base font-bold ${colorClass(monthRate)}`}>
                {fmtRate(monthRate)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-foreground-400 mb-0.5">交易日</p>
              <p className="text-base font-bold text-foreground-950">{monthDays}天</p>
            </div>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 mt-2 mb-1">
            {weekDayLabels.map((d) => (
              <span key={d} className="text-[10px] text-foreground-400 text-center py-1">
                {d}
              </span>
            ))}
          </div>

          {/* Day cells */}
          {currentMonthData && (
            <div className="grid grid-cols-7 gap-y-1">
              {Array.from({ length: currentMonthData.startDayIndex }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}
              {currentMonthData.daily.map((d) => (
                <div
                  key={d.day}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-colors ${
                    d.isTrade && d.profit !== null
                      ? d.profit > 0
                        ? "bg-[#E63946]/8"
                        : d.profit < 0
                        ? "bg-[#2D9F4C]/8"
                        : "bg-background-50"
                      : "bg-transparent"
                  }`}
                >
                  <span
                    className={`text-[12px] font-semibold leading-none ${
                      d.isTrade ? "text-foreground-800" : "text-foreground-300"
                    }`}
                  >
                    {d.day}
                  </span>
                  {d.isTrade && d.rate !== null && (
                    <span className={`text-[9px] font-semibold leading-none mt-0.5 ${colorClass(d.rate)}`}>
                      {fmtRate(d.rate)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-100">
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#E63946]/8" />
              <span className="text-[10px] text-foreground-500">盈利</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#2D9F4C]/8" />
              <span className="text-[10px] text-foreground-500">亏损</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-background-50 border border-background-200" />
              <span className="text-[10px] text-foreground-500">非交易日</span>
            </div>
          </div>
        </div>
      </div>

      {/* === Daily Table === */}
      {mode === "day" && (
        <div className="px-4">
          <div className="bg-background-100 rounded-t-xl px-4 py-3 grid grid-cols-5 gap-2">
            <span className="text-[10px] font-medium text-foreground-400">日期</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">当日收益</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">收益率</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">累计收益</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">累计收益率</span>
          </div>
          <div className="bg-white rounded-b-xl overflow-hidden">
            {tradeDays.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-foreground-400">
                该月暂无交易数据
              </div>
            ) : (
              tradeDays.map((d, i) => (
                <div
                  key={d.day}
                  className={`grid grid-cols-5 gap-2 px-4 py-3 ${
                    i < tradeDays.length - 1 ? "border-b border-background-100" : ""
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-foreground-700">
                      {String(currentMonth).padStart(2, "0")}-{String(d.day).padStart(2, "0")}
                    </span>
                    <span className="text-[10px] text-foreground-400">{d.weekDay}</span>
                  </div>
                  <span className={`text-xs font-semibold text-right ${colorClass(d.profit)}`}>
                    {fmtMoney(d.profit)}
                  </span>
                  <span className={`text-xs font-semibold text-right ${colorClass(d.rate)}`}>
                    {fmtRate(d.rate)}
                  </span>
                  <span className={`text-xs font-semibold text-right ${colorClass(d.cumProfit)}`}>
                    {fmtMoney(d.cumProfit)}
                  </span>
                  <span className={`text-xs font-semibold text-right ${colorClass(d.cumRate)}`}>
                    {fmtRate(d.cumRate)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* === Week View with Navigation === */}
      {mode === "week" && (
        <div className="px-4">
          {/* Week Navigator */}
          <div className="flex items-center justify-between mb-3 px-2">
            <button
              onClick={handlePrevWeek}
              disabled={!canGoPrevWeek}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                canGoPrevWeek ? "bg-white hover:bg-background-100 text-foreground-600" : "bg-background-50 text-foreground-300 cursor-not-allowed"
              }`}
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {currentWeek ? (
              <div className="text-center">
                <span className="text-sm font-bold text-foreground-950">
                  {currentWeek.label}
                </span>
                <div className="flex items-center justify-center gap-3 mt-1">
                  <span className={`text-xs font-semibold ${colorClass(currentWeek.profit)}`}>
                    周收益 {fmtMoney(currentWeek.profit)}
                  </span>
                  <span className="text-[10px] text-foreground-400">
                    ({currentWeek.days.filter((d) => d.isTrade).length}个交易日)
                  </span>
                </div>
              </div>
            ) : (
              <span className="text-sm text-foreground-400">暂无数据</span>
            )}
            <button
              onClick={handleNextWeek}
              disabled={!canGoNextWeek}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                canGoNextWeek ? "bg-white hover:bg-background-100 text-foreground-600" : "bg-background-50 text-foreground-300 cursor-not-allowed"
              }`}
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>

          {/* Current week indicator */}
          {weeks.length > 0 && (
            <div className="flex items-center gap-1 justify-center mb-3">
              {weeks.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setWeekIdx(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all cursor-pointer ${
                    i === weekIdx ? "bg-primary-500 w-4" : "bg-background-300"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Week Table Header */}
          <div className="bg-background-100 rounded-t-xl px-4 py-3 grid grid-cols-4 gap-2">
            <span className="text-[10px] font-medium text-foreground-400">天数</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">日收益</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">累计收益</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">累计收益率</span>
          </div>

          {/* Week Table Body */}
          <div className="bg-white rounded-b-xl overflow-hidden">
            {currentWeek ? (
              currentWeek.days
                .filter((d) => d.isTrade)
                .map((d, i, arr) => (
                  <div
                    key={d.day}
                    className={`grid grid-cols-4 gap-2 px-4 py-3 ${
                      i < arr.length - 1 ? "border-b border-background-100" : ""
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-foreground-700">
                        {String(currentMonth).padStart(2, "0")}-{String(d.day).padStart(2, "0")}
                      </span>
                      <span className="text-[10px] text-foreground-400">{d.weekDay}</span>
                    </div>
                    <span className={`text-xs font-semibold text-right ${colorClass(d.profit)}`}>
                      {fmtMoney(d.profit)}
                    </span>
                    <span className={`text-xs font-semibold text-right ${colorClass(d.cumProfit)}`}>
                      {fmtMoney(d.cumProfit)}
                    </span>
                    <span className={`text-xs font-semibold text-right ${colorClass(d.cumRate)}`}>
                      {fmtRate(d.cumRate)}
                    </span>
                  </div>
                ))
            ) : (
              <div className="px-4 py-8 text-center text-sm text-foreground-400">
                该月暂无交易数据
              </div>
            )}
          </div>
        </div>
      )}

      {/* === Month View === */}
      {mode === "month" && (
        <div className="px-4">
          <div className="bg-background-100 rounded-t-xl px-4 py-3 grid grid-cols-5 gap-2">
            <span className="text-[10px] font-medium text-foreground-400">月份</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">月收益</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">收益率</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">累计收益</span>
            <span className="text-[10px] font-medium text-foreground-400 text-right">累计收益率</span>
          </div>
          <div className="bg-white rounded-b-xl overflow-hidden">
            {monthRows.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-foreground-400">暂无月度数据</div>
            ) : (
              monthRows.map((m, i) => (
                <div
                  key={m.sortKey}
                  className={`grid grid-cols-5 gap-2 px-4 py-3 ${
                    i < monthRows.length - 1 ? "border-b border-background-100" : ""
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-foreground-700">{m.month}</span>
                    <span className="text-[10px] text-foreground-400">{m.days}天</span>
                  </div>
                  <span className={`text-xs font-semibold text-right ${colorClass(m.profit)}`}>
                    {fmtMoney(m.profit)}
                  </span>
                  <span className={`text-xs font-semibold text-right ${colorClass(m.rate)}`}>
                    {fmtRate(m.rate)}
                  </span>
                  <span className={`text-xs font-semibold text-right ${colorClass(m.cumProfit)}`}>
                    {fmtMoney(m.cumProfit)}
                  </span>
                  <span className={`text-xs font-semibold text-right ${colorClass(m.cumRate)}`}>
                    {fmtRate(m.cumRate)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="px-4 mt-4 pb-8">
        <p className="text-[10px] text-foreground-400 text-center">
          模拟 · 仅供学习参考，不构成投资建议
        </p>
      </div>
    </div>
  );
}
